// Integração Conta Azul: sync de contas a pagar/receber, saldos, reconciliações.
// Inclui banner do cron de reconciliação 6h BRT, cut-over config inline,
// dry-runs por endpoint e tabela de histórico das últimas execuções.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Alert, Tag, Typography, Space, Flex, Row, Col, Statistic,
  Checkbox, DatePicker, Input, Collapse, Table, App as AntApp,
} from 'antd';
import {
  LinkOutlined, ReloadOutlined, SyncOutlined, DisconnectOutlined,
  SearchOutlined, DownloadOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  ClockCircleOutlined, BankOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../../config';
import { fmtDataBr } from '../../utils/dataFmt';
import { getToken } from '../../utils/auth';
import CutOverConfig from './CutOverConfig';
import KpiCard from './KpiCard';
import DetalheLista from './DetalheLista';
import ListaSimples from './ListaSimples';
import type { Status, PeriodoSync } from './types';

const { Title, Text } = Typography;

export default function IntegracaoContaAzul() {
  const { message, modal } = AntApp.useApp();
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState<any>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<{ total: number; inseridas: number; atualizadas: number } | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const [sincCp, setSincCp] = useState(false);
  const [resultSyncCp, setResultSyncCp] = useState<any>(null);
  const [periodoSyncCp, setPeriodoSyncCp] = useState<PeriodoSync>({
    de: `${new Date().getFullYear() - 1}-01-01`,
    ate: `${new Date().getFullYear() + 1}-12-31`,
  });
  const [matchXlsx, setMatchXlsx] = useState(true);

  const [sincCr, setSincCr] = useState(false);
  const [resultSyncCr, setResultSyncCr] = useState<any>(null);
  const [periodoSyncCr, setPeriodoSyncCr] = useState<PeriodoSync>({
    de: `${new Date().getFullYear() - 1}-01-01`,
    ate: `${new Date().getFullYear() + 1}-12-31`,
  });
  const [matchNotion, setMatchNotion] = useState(true);

  const [recCr, setRecCr] = useState<any>(null);
  const [recCrLoading, setRecCrLoading] = useState(false);

  const [saldosLoading, setSaldosLoading] = useState(false);
  const [saldosResult, setSaldosResult] = useState<any>(null);
  const [saldosSyncLoading, setSaldosSyncLoading] = useState(false);
  const [saldosSyncResult, setSaldosSyncResult] = useState<any>(null);
  const [incluirTeste, setIncluirTeste] = useState(false);

  const [recCp, setRecCp] = useState<any>(null);
  const [recLoading, setRecLoading] = useState(false);
  const [recPeriodo, setRecPeriodo] = useState('');

  const [reconciliando, setReconciliando] = useState(false);
  const [reconciliacaoAtual, setReconciliacaoAtual] = useState<any>(null);
  const [historicoReconc, setHistoricoReconc] = useState<any[]>([]);

  async function carregarHistoricoReconc() {
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/admin/reconciliacao-log?limit=10`,
        { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setHistoricoReconc(Array.isArray(d) ? d : []);
      const emAndamento = Array.isArray(d) && d.find((x: any) => !x.terminou_em);
      if (emAndamento) { setReconciliacaoAtual(emAndamento); setReconciliando(true); }
      else {
        setReconciliando(false);
        if (reconciliacaoAtual && !reconciliacaoAtual.terminou_em) setReconciliacaoAtual(d[0] || null);
      }
    } catch (e: any) {
      console.warn('Falha ao carregar histórico de reconciliação:', e.message);
    }
  }

  useEffect(() => {
    if (!reconciliando) return;
    const t = setInterval(() => carregarHistoricoReconc(), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciliando]);

  function dispararReconciliacao() {
    if (reconciliando) return;
    modal.confirm({
      title: 'Disparar reconciliação completa AGORA?',
      content: (
        <div>
          <Text style={{ fontSize: 12 }}>
            Vai puxar fresco do Conta Azul (contas a pagar, receber, saldos, baixas dos últimos 7 dias).
            Dura ~5-10 min. Pode fechar a tela — roda em background.
          </Text>
          <Alert
            type="info" style={{ marginTop: 8 }}
            message={<Text style={{ fontSize: 11 }}>O cron diário 6h BRT já está ativo; isso é só pra antecipar.</Text>}
          />
        </div>
      ),
      okText: 'Disparar', cancelText: 'Cancelar',
      onOk: async () => {
        setReconciliando(true); setErro(null);
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/admin/reconciliar-tudo`, {}, { headers: { Authorization: token } });
          await carregarHistoricoReconc();
        } catch (e: any) {
          setErro('Falha ao disparar reconciliação: ' + (e.response?.data?.error || e.message));
          setReconciliando(false);
        }
      },
    });
  }

  async function carregarStatus() {
    setLoading(true);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/conta-azul/status`, { headers: { Authorization: token } });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      setStatus(d);
    } catch (e: any) {
      setErro(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    carregarStatus();
    carregarHistoricoReconc();
    const params = new URLSearchParams(window.location.search);
    if (params.get('ca_conectado') === '1') {
      message.success('Conta Azul conectada com sucesso!');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('ca_erro')) {
      message.error('Falha ao conectar Conta Azul: ' + params.get('ca_erro'));
      window.history.replaceState({}, '', window.location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function conectarContaAzul() {
    const returnUrl = `${window.location.origin}/?aba=admin-integracoes`;
    window.location.href = `${API_URL}/conta-azul/auth/start?return_url=${encodeURIComponent(returnUrl)}`;
  }

  function desconectarContaAzul() {
    modal.confirm({
      title: 'Desconectar Conta Azul?',
      content: 'Você terá que reautorizar pra sincronizar de novo.',
      okText: 'Desconectar', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const token = await getToken();
          await axios.post(`${API_URL}/conta-azul/auth/desconectar`, {}, { headers: { Authorization: token } });
          await carregarStatus();
          message.success('Conta Azul desconectada.');
        } catch (e: any) {
          message.error('Erro: ' + (e.response?.data?.error || e.message));
        }
      },
    });
  }

  async function testarConexao() {
    setTestando(true); setErro(null); setResultadoTeste(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/conta-azul/teste-conexao`, {}, { headers: { Authorization: token }, timeout: 60_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error);
      else { setResultadoTeste(d); await carregarStatus(); }
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setTestando(false); }
  }

  async function sincronizarCategorias() {
    setSincronizando(true); setErro(null); setResultadoSync(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/conta-azul/sync-categorias`, {}, { headers: { Authorization: token }, timeout: 60_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setResultadoSync(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setSincronizando(false); }
  }

  async function sincronizarContasPagar(dryRun: boolean) {
    setSincCp(true); setErro(null); setResultSyncCp(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/conta-azul/sync-contas-pagar`, {
        data_vencimento_de: periodoSyncCp.de, data_vencimento_ate: periodoSyncCp.ate,
        dry_run: dryRun, match_xlsx: matchXlsx,
      }, { headers: { Authorization: token }, timeout: 300_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setResultSyncCp(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setSincCp(false); }
  }

  async function sincronizarContasReceber(dryRun: boolean) {
    setSincCr(true); setErro(null); setResultSyncCr(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/conta-azul/sync-contas-receber`, {
        data_vencimento_de: periodoSyncCr.de, data_vencimento_ate: periodoSyncCr.ate,
        dry_run: dryRun, match_notion: matchNotion,
      }, { headers: { Authorization: token }, timeout: 300_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setResultSyncCr(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setSincCr(false); }
  }

  async function listarSaldos() {
    setSaldosLoading(true); setErro(null); setSaldosResult(null);
    try {
      const token = await getToken();
      const q = incluirTeste ? '?incluir_teste=1' : '';
      const r = await axios.get(`${API_URL}/conta-azul/saldos-bancarios${q}`, { headers: { Authorization: token }, timeout: 120_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setSaldosResult(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setSaldosLoading(false); }
  }

  async function sincronizarSaldos(dryRun: boolean) {
    setSaldosSyncLoading(true); setErro(null); setSaldosSyncResult(null);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/conta-azul/sync-saldos-bancarios`,
        { dry_run: dryRun, incluir_teste: incluirTeste },
        { headers: { Authorization: token }, timeout: 120_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setSaldosSyncResult(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setSaldosSyncLoading(false); }
  }

  async function reconciliarReceberNotionVsCa() {
    setRecCrLoading(true); setErro(null); setRecCr(null);
    try {
      const token = await getToken();
      const r = await axios.get(`${API_URL}/conta-azul/reconcile-receber-notion-vs-ca`,
        { headers: { Authorization: token }, timeout: 120_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setRecCr(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setRecCrLoading(false); }
  }

  async function reconciliarContasPagar() {
    setRecLoading(true); setErro(null); setRecCp(null);
    try {
      const token = await getToken();
      const q = recPeriodo ? `?periodo=${encodeURIComponent(recPeriodo)}` : '';
      const r = await axios.get(`${API_URL}/conta-azul/reconcile-contas-pagar${q}`,
        { headers: { Authorization: token }, timeout: 120_000 });
      const d = r.data.body ? JSON.parse(r.data.body) : r.data;
      if (d.error) setErro(d.error); else setRecCp(d);
    } catch (e: any) { setErro(e.response?.data?.error || e.message); }
    finally { setRecLoading(false); }
  }

  const fmt = (v: any) => {
    const n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return 'R$ 0,00';
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });
  };

  const horasAteExpirar = status?.expires_at
    ? Math.max(0, (new Date(status.expires_at).getTime() - Date.now()) / 3600000)
    : null;

  const colsHistorico: ColumnsType<any> = [
    { title: 'Quando', dataIndex: 'iniciado_em', key: 'q', render: (v) => new Date(v).toLocaleString('pt-BR') },
    { title: 'Disparada por', dataIndex: 'disparada_por', key: 'd' },
    { title: 'Duração', dataIndex: 'duracao_ms', key: 'dur', align: 'center', width: 80,
      render: (v) => v ? `${(v / 1000).toFixed(0)}s` : '—' },
    {
      title: 'Status', key: 'st', align: 'center', width: 100,
      render: (_, r) => r.terminou_em == null
        ? <Tag icon={<ClockCircleOutlined />} color="processing">Em andamento</Tag>
        : r.sucesso ? <Tag icon={<CheckCircleOutlined />} color="success">OK</Tag>
                    : <Tag icon={<ExclamationCircleOutlined />} color="error">falhou</Tag>,
    },
    {
      title: 'Etapas', dataIndex: 'etapas', key: 'et',
      render: (etapas: any) => {
        const arr = Array.isArray(etapas) ? etapas : [];
        const ok = arr.filter((e: any) => e.ok).length;
        const fail = arr.filter((e: any) => !e.ok).length;
        return arr.length === 0 ? '— iniciando…' : `${ok} OK${fail ? ` · ${fail} falha${fail > 1 ? 's' : ''}` : ''}`;
      },
    },
  ];

  return (
    <div>
      {erro && (
        <Alert type="error" message={erro} showIcon closable onClose={() => setErro(null)} style={{ marginBottom: 14 }} />
      )}

      {/* Sprint F12 — Reconciliação unificada */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
          <div>
            <Title level={5} style={{ margin: 0, color: '#020D7F' }}>🔄 Reconciliação automática</Title>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              Puxa fresco do <strong>Conta Azul</strong> (o Notion não é mais fonte do CR).
              <strong> Cron diário 6h BRT</strong> já ativo — clique no botão só pra antecipar.
            </Text>
          </div>
          <Button
            type="primary" size="middle"
            icon={<ReloadOutlined spin={reconciliando} />}
            loading={reconciliando}
            style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
            onClick={dispararReconciliacao}
          >
            {reconciliando ? 'Reconciliando...' : 'Reconciliar agora'}
          </Button>
        </Flex>

        {reconciliando && reconciliacaoAtual && (
          <Alert
            type="info" style={{ marginTop: 14 }}
            message={`⏳ Em andamento — iniciada ${new Date(reconciliacaoAtual.iniciado_em).toLocaleString('pt-BR')}`}
            description={
              Array.isArray(reconciliacaoAtual.etapas) && reconciliacaoAtual.etapas.length > 0 && (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  {reconciliacaoAtual.etapas.map((e: any, i: number) => (
                    <Text key={i} style={{ fontSize: 11, color: e.ok ? '#0a7f4a' : '#B91626' }}>
                      {e.ok ? '✓' : '✗'} {e.nome}
                      {e.duracao_ms != null && (
                        <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                          ({(e.duracao_ms / 1000).toFixed(1)}s)
                        </Text>
                      )}
                      {!e.ok && e.erro && <Text style={{ color: '#B91626', marginLeft: 6 }}> — {e.erro}</Text>}
                    </Text>
                  ))}
                </Space>
              )
            }
          />
        )}

        {historicoReconc.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Text strong style={{ fontSize: 11, color: '#666', display: 'block', marginBottom: 6 }}>
              📊 Últimas execuções
            </Text>
            <Table
              rowKey="id" columns={colsHistorico} dataSource={historicoReconc}
              size="small" pagination={false}
            />
          </div>
        )}
      </Card>

      {/* Card de Cut-over: data de migração CA → sistema */}
      <CutOverConfig />

      {/* Card Conta Azul */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16} style={{ marginBottom: 14 }}>
          <div>
            <Title level={5} style={{ margin: 0, color: '#020D7F' }}>📊 Conta Azul</Title>
            <Text type="secondary" style={{ fontSize: 11 }}>
              Sync de contas a pagar/receber e saldos bancários.
              Ambiente: <strong>{status?.ambiente || '—'}</strong>
              {' · '}API: <code style={{ fontSize: 10 }}>{status?.api_base || '—'}</code>
            </Text>
          </div>
          {loading ? <Tag>Carregando…</Tag>
            : status?.conectado ? <Tag color="success">● Conectado</Tag>
            : <Tag color="error">○ Sem token</Tag>}
        </Flex>

        {status && (
          <Card size="small" style={{ background: '#F8F9FB', marginBottom: 14 }} bodyStyle={{ padding: 10 }}>
            <Row gutter={[12, 4]}>
              <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>Token expira em</Text></Col>
              <Col span={18}>
                <Text strong style={{ color: horasAteExpirar && horasAteExpirar < 0.1 ? '#B91626' : '#222', fontSize: 11 }}>
                  {status.expires_at
                    ? `${horasAteExpirar?.toFixed(2)} h (${new Date(status.expires_at).toLocaleString('pt-BR')})`
                    : '—'}
                </Text>
              </Col>
              <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>Atualizado em</Text></Col>
              <Col span={18}><Text strong style={{ fontSize: 11 }}>{status.atualizado_em ? new Date(status.atualizado_em).toLocaleString('pt-BR') : '—'}</Text></Col>
              <Col span={6}><Text type="secondary" style={{ fontSize: 11 }}>Atualizado por</Text></Col>
              <Col span={18}><Text strong style={{ fontSize: 11 }}>{status.atualizado_por || '—'}</Text></Col>
            </Row>
          </Card>
        )}

        <Space wrap>
          <Button type="primary" icon={<LinkOutlined />} onClick={conectarContaAzul}
            style={{ background: '#0a7f4a', borderColor: '#0a7f4a' }}
            title="Inicia fluxo OAuth — abre login do CA pra autorizar o app">
            Conectar Conta Azul
          </Button>
          <Button type="primary" icon={<SearchOutlined />} loading={testando} onClick={testarConexao}>
            Testar conexão
          </Button>
          <Button type="primary" ghost icon={<SyncOutlined />} loading={sincronizando} onClick={sincronizarCategorias}>
            Sincronizar categorias
          </Button>
          {status?.conectado && (
            <Button danger icon={<DisconnectOutlined />} onClick={desconectarContaAzul}>
              Desconectar
            </Button>
          )}
        </Space>

        {resultadoTeste && (
          <Alert
            type="success" showIcon style={{ marginTop: 14 }}
            message={`Conexão OK — HTTP ${resultadoTeste.status_http}`}
            description={
              <div>
                <Text style={{ fontSize: 12 }}>
                  <strong>{resultadoTeste.total_categorias}</strong> categorias na conta.
                  Token renovado, expira em {fmtDataBr(String(resultadoTeste.expires_at).slice(0, 10))}.
                </Text>
              </div>
            }
          />
        )}

        {resultadoSync && (
          <Alert
            type="info" showIcon style={{ marginTop: 14 }}
            message="Categorias sincronizadas"
            description={`${resultadoSync.total} total · ${resultadoSync.inseridas} novas · ${resultadoSync.atualizadas} atualizadas.`}
          />
        )}
      </Card>

      <Collapse
        size="small"
        items={[{
          key: 'avancadas',
          label: (
            <Text strong style={{ color: '#0930D0' }}>
              🔧 Ferramentas avançadas · sync individuais, dry-runs e reconciliação detalhada
            </Text>
          ),
          children: (
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              {/* F10.2 Sync CP */}
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>📥 Sync · Contas a Pagar (CA → sistema)</Text>}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                  Puxa <code>contas-a-pagar</code> da CA. <strong>match-and-migrate</strong>:
                  procura XLSX equivalente e <strong>promove</strong> em vez de duplicar.
                </Text>
                <Checkbox checked={matchXlsx} onChange={e => setMatchXlsx(e.target.checked)} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11 }}>
                    <strong>Match XLSX</strong> (default) — promove XLSX equivalente.
                  </Text>
                </Checkbox>
                <Flex gap={10} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11 }}>Vencimento de:</Text>
                  <DatePicker size="small" format="DD/MM/YYYY"
                    value={periodoSyncCp.de ? dayjs(periodoSyncCp.de) : null}
                    onChange={(d) => setPeriodoSyncCp({ ...periodoSyncCp, de: d ? d.format('YYYY-MM-DD') : '' })} />
                  <Text style={{ fontSize: 11 }}>até:</Text>
                  <DatePicker size="small" format="DD/MM/YYYY"
                    value={periodoSyncCp.ate ? dayjs(periodoSyncCp.ate) : null}
                    onChange={(d) => setPeriodoSyncCp({ ...periodoSyncCp, ate: d ? d.format('YYYY-MM-DD') : '' })} />
                </Flex>
                <Space>
                  <Button size="small" type="primary" ghost onClick={() => sincronizarContasPagar(true)} loading={sincCp}>
                    🔍 Dry-run
                  </Button>
                  <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={() => sincronizarContasPagar(false)} loading={sincCp}>
                    Aplicar sync
                  </Button>
                </Space>
                {resultSyncCp && (
                  <Alert
                    type={resultSyncCp.dry_run ? 'warning' : 'success'} showIcon style={{ marginTop: 12 }}
                    message={resultSyncCp.dry_run ? 'Dry-run completo (nada aplicado)' : 'Sync aplicado'}
                    description={
                      <Row gutter={[8, 4]} style={{ fontSize: 11 }}>
                        <Col span={12}><Text type="secondary">Páginas processadas:</Text> <strong>{resultSyncCp.paginas}</strong></Col>
                        <Col span={12}><Text type="secondary">Itens totais:</Text> <strong>{resultSyncCp.itens_totais}</strong></Col>
                        <Col span={12}><Text type="secondary">🆕 Novos:</Text> <Text strong style={{ color: '#0a7f4a' }}>{resultSyncCp.novos ?? 0}</Text></Col>
                        <Col span={12}><Text type="secondary">♻ Promovidos:</Text> <Text strong style={{ color: '#0930D0' }}>{resultSyncCp.promovidos ?? 0}</Text></Col>
                        <Col span={12}><Text type="secondary">🔁 Atualizados:</Text> <strong>{resultSyncCp.atualizados ?? 0}</strong></Col>
                        <Col span={12}><Text type="secondary">⚠ Ambíguos:</Text> <Text strong style={{ color: resultSyncCp.ambiguos > 0 ? '#B07500' : '#222' }}>{resultSyncCp.ambiguos ?? 0}</Text></Col>
                        <Col span={12}><Text type="secondary">❌ Erros:</Text> <Text strong style={{ color: resultSyncCp.erros > 0 ? '#B91626' : '#222' }}>{resultSyncCp.erros}</Text></Col>
                      </Row>
                    }
                  />
                )}
              </Card>

              {/* F10.2 Reconcile CP */}
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>⚖ Reconciliar Contas a Pagar (CA × XLSX)</Text>}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
                  Compara lançamentos da CA com os que vieram da planilha.
                  Match: <code>(data_competencia + fornecedor + valor)</code>.
                </Text>
                <Flex gap={10} align="center" style={{ marginBottom: 10 }} wrap="wrap">
                  <Text style={{ fontSize: 11 }}>Filtrar período (YYYY/MM ou YYYY):</Text>
                  <Input size="small" value={recPeriodo} onChange={e => setRecPeriodo(e.target.value)}
                    placeholder="ex: 2026/04 ou 2026" style={{ width: 140 }} />
                  <Button size="small" type="primary" onClick={reconciliarContasPagar} loading={recLoading}>
                    ⚖ Reconciliar
                  </Button>
                </Flex>
                {recCp && (
                  <div style={{ marginTop: 8 }}>
                    <Row gutter={6} style={{ marginBottom: 12 }}>
                      <Col span={4}><KpiCard rotulo="CA" valor={recCp.totais.ca} /></Col>
                      <Col span={4}><KpiCard rotulo="XLSX" valor={recCp.totais.xlsx} /></Col>
                      <Col span={4}><KpiCard rotulo="Idênticos" valor={recCp.totais.identicos} cor="#0a7f4a" /></Col>
                      <Col span={4}><KpiCard rotulo="Divergentes" valor={recCp.totais.divergentes} cor="#B07500" /></Col>
                      <Col span={4}><KpiCard rotulo="Só na CA" valor={recCp.totais.apenas_ca} cor="#0930D0" /></Col>
                      <Col span={4}><KpiCard rotulo="Só XLSX" valor={recCp.totais.apenas_xlsx} cor="#666" /></Col>
                    </Row>
                    {recCp.apenas_ca?.length > 0 && <DetalheLista titulo={`Apenas na CA (${recCp.apenas_ca.length})`} cor="blue"><ListaSimples itens={recCp.apenas_ca.slice(0, 30)} fmt={fmt} /></DetalheLista>}
                    {recCp.apenas_xlsx?.length > 0 && <DetalheLista titulo={`Apenas no XLSX (${recCp.apenas_xlsx.length})`} cor="default"><ListaSimples itens={recCp.apenas_xlsx.slice(0, 30)} fmt={fmt} /></DetalheLista>}
                    {recCp.divergentes?.length > 0 && <DetalheLista titulo={`Divergentes (${recCp.divergentes.length})`} cor="gold"><ListaSimples itens={recCp.divergentes.slice(0, 30)} fmt={fmt} /></DetalheLista>}
                  </div>
                )}
              </Card>

              {/* F10.3 Sync CR */}
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>📤 Sync · Contas a Receber (CA → sistema)</Text>}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                  Puxa <code>contas-a-receber</code> da CA. <strong>match-and-merge</strong>:
                  preserva enriquecimentos do plano e atualiza <code>valor_recebido</code>, <code>data_recebimento</code>, <code>status_ca</code>.
                </Text>
                <Checkbox checked={matchNotion} onChange={e => setMatchNotion(e.target.checked)} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11 }}><strong>Casar com parcelas do plano</strong> (default; match local, não lê o Notion)</Text>
                </Checkbox>
                <Flex gap={10} align="center" wrap="wrap" style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11 }}>Vencimento de:</Text>
                  <DatePicker size="small" format="DD/MM/YYYY"
                    value={periodoSyncCr.de ? dayjs(periodoSyncCr.de) : null}
                    onChange={(d) => setPeriodoSyncCr({ ...periodoSyncCr, de: d ? d.format('YYYY-MM-DD') : '' })} />
                  <Text style={{ fontSize: 11 }}>até:</Text>
                  <DatePicker size="small" format="DD/MM/YYYY"
                    value={periodoSyncCr.ate ? dayjs(periodoSyncCr.ate) : null}
                    onChange={(d) => setPeriodoSyncCr({ ...periodoSyncCr, ate: d ? d.format('YYYY-MM-DD') : '' })} />
                </Flex>
                <Space>
                  <Button size="small" type="primary" ghost onClick={() => sincronizarContasReceber(true)} loading={sincCr}>
                    🔍 Dry-run
                  </Button>
                  <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={() => sincronizarContasReceber(false)} loading={sincCr}>
                    Aplicar sync
                  </Button>
                </Space>
                {resultSyncCr && (
                  <Alert
                    type={resultSyncCr.dry_run ? 'warning' : 'success'} showIcon style={{ marginTop: 12 }}
                    message={resultSyncCr.dry_run ? 'Dry-run (nada aplicado)' : 'Sync aplicado'}
                    description={
                      <Row gutter={[8, 4]} style={{ fontSize: 11 }}>
                        <Col span={12}><Text type="secondary">Páginas/itens:</Text> <strong>{resultSyncCr.paginas} / {resultSyncCr.itens_totais}</strong></Col>
                        <Col span={12}><Text type="secondary">🆕 Novos:</Text> <Text strong style={{ color: '#0a7f4a' }}>{resultSyncCr.novos ?? 0}</Text></Col>
                        <Col span={12}><Text type="secondary">♻ Promovidos:</Text> <Text strong style={{ color: '#0930D0' }}>{resultSyncCr.promovidos ?? 0}</Text></Col>
                        <Col span={12}><Text type="secondary">🔁 Atualizados:</Text> <strong>{resultSyncCr.atualizados ?? 0}</strong></Col>
                        <Col span={12}><Text type="secondary">⚠ Ambíguos:</Text> <strong>{resultSyncCr.ambiguos ?? 0}</strong></Col>
                        <Col span={12}><Text type="secondary">❌ Erros:</Text> <strong>{resultSyncCr.erros}</strong></Col>
                      </Row>
                    }
                  />
                )}
              </Card>

              {/* F10.3 Reconcile CR */}
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}>⚖ Reconciliar Contas a Receber (Notion × CA) — histórico</Text>}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
                  Compara <code>financeiro.contas_a_receber</code> com origem='notion' vs 'conta_azul_api' (comparador local; o Notion é histórico).
                </Text>
                <Button size="small" type="primary" onClick={reconciliarReceberNotionVsCa} loading={recCrLoading}>
                  ⚖ Reconciliar
                </Button>
                {recCr && (
                  <div style={{ marginTop: 12 }}>
                    <Row gutter={6}>
                      <Col span={4}><KpiCard rotulo="Notion" valor={recCr.totais.notion} /></Col>
                      <Col span={4}><KpiCard rotulo="CA" valor={recCr.totais.ca} /></Col>
                      <Col span={4}><KpiCard rotulo="Idênticos" valor={recCr.totais.identicos} cor="#0a7f4a" /></Col>
                      <Col span={4}><KpiCard rotulo="Divergentes" valor={recCr.totais.divergentes} cor="#B07500" /></Col>
                      <Col span={4}><KpiCard rotulo="Só Notion" valor={recCr.totais.apenas_notion} cor="#7C3AED" /></Col>
                      <Col span={4}><KpiCard rotulo="Só CA" valor={recCr.totais.apenas_ca} cor="#0930D0" /></Col>
                    </Row>
                  </div>
                )}
              </Card>

              {/* F10.4 Saldos */}
              <Card size="small" title={<Text strong style={{ color: '#020D7F' }}><BankOutlined /> Sync · Saldos Bancários</Text>}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>
                  Lista contas financeiras da CA e busca saldo atual. Somatório vira registro novo em <code>fluxo_saldo_inicial</code>.
                </Text>
                <Checkbox checked={incluirTeste} onChange={e => setIncluirTeste(e.target.checked)} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11 }}>Incluir contas de <strong>teste/sandbox</strong></Text>
                </Checkbox>
                <Space wrap style={{ marginBottom: 10 }}>
                  <Button size="small" type="primary" ghost onClick={listarSaldos} loading={saldosLoading}>
                    🔍 Listar saldos atuais
                  </Button>
                  <Button size="small" onClick={() => sincronizarSaldos(true)} loading={saldosSyncLoading}
                    style={{ color: '#B07500', borderColor: '#B07500' }}>
                    🔍 Dry-run
                  </Button>
                  <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={() => sincronizarSaldos(false)} loading={saldosSyncLoading}>
                    Sincronizar saldo no fluxo de caixa
                  </Button>
                </Space>
                {saldosResult && (
                  <Card size="small" style={{ marginTop: 12, background: '#FAFBFF' }} bodyStyle={{ padding: 10 }}>
                    <Statistic
                      title="Total"
                      value={(saldosResult.total ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      valueStyle={{ color: '#0a7f4a', fontSize: 16 }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {(saldosResult.contas?.filter((c: any) => !c.ignorada).length) || 0} conta(s) somada(s)
                      {saldosResult.contas?.some((c: any) => c.ignorada) && (
                        <span style={{ color: '#B07500' }}>
                          {' '}· {saldosResult.contas.filter((c: any) => c.ignorada).length} ignorada(s)
                        </span>
                      )}
                    </Text>
                    <Table
                      size="small" rowKey="id" style={{ marginTop: 10 }}
                      pagination={false}
                      dataSource={saldosResult.contas || []}
                      columns={[
                        {
                          title: 'Conta', dataIndex: 'nome', key: 'n',
                          render: (v, r: any) => (
                            <Text>
                              {v}
                              {r.ignorada && <Tag color="warning" style={{ marginLeft: 6 }}>ignorada</Tag>}
                            </Text>
                          ),
                        },
                        { title: 'Tipo', dataIndex: 'tipo', key: 't' },
                        { title: 'Ativa', dataIndex: 'ativa', key: 'a', align: 'center', render: (v) => v ? '✓' : '—' },
                        {
                          title: 'Saldo', dataIndex: 'saldo', key: 's', align: 'right',
                          render: (v, r: any) => (
                            <Text strong style={{
                              color: r.ignorada ? '#888' : ((v || 0) >= 0 ? '#0a7f4a' : '#B91626'),
                              textDecoration: r.ignorada ? 'line-through' : 'none',
                            }}>
                              {v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </Text>
                          ),
                        },
                      ]}
                    />
                  </Card>
                )}
                {saldosSyncResult && (
                  <Alert
                    type={saldosSyncResult.dry_run ? 'warning' : 'success'} showIcon style={{ marginTop: 12 }}
                    message={saldosSyncResult.dry_run ? 'Dry-run (nada aplicado)' : 'Saldo registrado no fluxo de caixa'}
                    description={
                      <Text style={{ fontSize: 11 }}>
                        Data: <strong>{saldosSyncResult.data_ref}</strong> ·
                        {' '}{saldosSyncResult.contas?.length || 0} conta(s), total{' '}
                        <strong style={{ color: '#0a7f4a' }}>
                          {(saldosSyncResult.total ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </strong>
                      </Text>
                    }
                  />
                )}
              </Card>
            </Space>
          ),
        }]}
        style={{ marginBottom: 14 }}
      />

      <Alert
        type="info" showIcon
        message={<Text style={{ fontSize: 11 }}>Próximos: SES envio NF/BM, OFX bancário (ingestão + classificação + conciliação).</Text>}
        style={{ background: '#FAFBFF', borderStyle: 'dashed' }}
      />
    </div>
  );
}
